import { useSWRConfig } from 'swr';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import EducatorCourses from '@cont/EducatorProfile/EducatorCourses';
import SEO from '@seo/index';
import { fetchUserProfileData } from '@api/fetchers/profile';
import { fetchCoursesWithFilters } from '@api/fetchers/courses';
import useCoursesByFilters from '@api/hooks/courses/useCoursesByFilters';
import { getIsLoggedIn } from '@selectors/auth';
import Layout from '@layout/index';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const getFiltersApplied = (query) => {
  return {
    topic: { id: query.topic },
    show_only: { id: query.courses },
    goal: { id: query.goal },
    language: { id: query.language },
    filter_by: { id: query.type }
  };
};

const titleTypeMap = { special: 'trial classes', batch_groups: 'batches' };
const BatchGroups = ({
  profileInfo,
  coursesByFilters: fallbackCourses,
  filtersAppliedOnServer
}) => {
  const router = useRouter();
  const { cache } = useSWRConfig();

  const isLoggedIn = useSelector(getIsLoggedIn);
  let { authorID, type } = router.query;
  const titleType = titleTypeMap[type] || 'batches';

  if (!authorID.startsWith('@')) authorID = `@${authorID}`;

  const [filtersApplied, setFiltersApplied] = useState(filtersAppliedOnServer);
  const [sortsApplied, setSortsApplied] = useState(null);

  const { firstName, lastName, description } = profileInfo;

  const isPlatinumEducator = !!(
    profileInfo?.isPlatformEducator || profileInfo?.isPlatinumEducator
  );

  const title = `${firstName} ${lastName} ${titleType} | Unacademy`;
  const url = `https://unacademy.com/${authorID}`;

  const {
    coursesByFilters,
    mutate: refetchData,
    isValidating,
    setSize
  } = useCoursesByFilters(
    { userName: authorID?.slice(1), type },
    { filtersApplied, sortsApplied },
    { fallbackData: fallbackCourses }
  );

  useEffect(() => {
    if (isLoggedIn) {
      cache.clear();
      setSize(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    const currentFilter = getFiltersApplied(router.query);

    if (!isEqual(currentFilter, filtersApplied)) {
      setFiltersApplied(currentFilter);
      cache.clear();
      setSize(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query]);

  return (
    <>
      <SEO
        title={title}
        url={url}
        description={description}
        openGraph={{ title, description, url }}
      />
      <EducatorCourses
        authorID={authorID}
        filtersApplied={filtersApplied}
        setFiltersApplied={setFiltersApplied}
        profileInfo={profileInfo}
        setSortsApplied={setSortsApplied}
        hideFilters
        titleType={titleType}
        filteredCoursesData={{
          coursesByFilters,
          updateCoursesListData: refetchData,
          isValidating,
          setSize
        }}
        isPlatinumEducator={isPlatinumEducator}
      />
    </>
  );
};

BatchGroups.pageInfo = {
  layoutOptions: {
    hideFooter: true
  },
  isMWebSupported: true
};

export const getStaticProps = async ({ params: query }) => {
  let userName = query.authorID;
  if (userName.startsWith('@')) userName = query.authorID?.slice(1);

  const profileInfo = await fetchUserProfileData(userName);
  if (!profileInfo || profileInfo.errorCode)
    return { notFound: true, revalidate: 60 };

  const { isEducator, isExEducator, isModerator } = profileInfo;
  const isEducatorProfile = isEducator || isExEducator || isModerator;

  if (!isEducatorProfile) return { notFound: true, revalidate: 60 };

  const filtersToApply = {
    topic: { id: '' },
    show_only: { id: '' },
    goal: { id: '' },
    language: { id: '' },
    filter_by: { id: '' }
  };
  const coursesByFilters = await fetchCoursesWithFilters(
    userName,
    filtersToApply
  );

  return {
    props: {
      profileInfo,
      coursesByFilters,
      filtersAppliedOnServer: filtersToApply
    },
    revalidate: 5 * 60
  };
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default BatchGroups;

BatchGroups.propTypes = {
  profileInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  coursesByFilters: PropTypes.objectOf(PropTypes.any).isRequired,
  filtersAppliedOnServer: PropTypes.objectOf(PropTypes.any).isRequired
};
BatchGroups.Layout = Layout;
BatchGroups.Wrapper = PageReduxProvider;
